import { Button, Skeleton } from "@heroui/react";
import dayjs from "dayjs";
import { Pencil, Trash } from "lucide-react";
import { useState } from "react";

import { PlainDateRangeField, PlainTextField, notify } from "@/components/kit";
import {
  useCreateSupplierClosure,
  useDeleteSupplierClosure,
  useGetSupplierClosures,
  useUpdateSupplierClosure,
} from "@/hooks/api/supplier";
import { CreateSupplierClosure, SupplierClosure } from "@/types/supplier";
import { getApiErrorMessage } from "@/utils/api-error";
import { formatDay } from "@/utils/date";

const MAX_CLOSURE_DAYS = 31;

type ClosureDraft = { startDate: string; endDate: string; reason: string };

const EMPTY_DRAFT: ClosureDraft = { startDate: "", endDate: "", reason: "" };

const draftError = ({ startDate, endDate }: ClosureDraft): string | null => {
  if (startDate === "" || endDate === "") return "Pick the closed days";

  const days = dayjs(endDate).diff(dayjs(startDate), "day") + 1;

  if (days > MAX_CLOSURE_DAYS)
    return `A closure spans at most ${MAX_CLOSURE_DAYS} days`;

  return null;
};

const toClosureBody = (draft: ClosureDraft): CreateSupplierClosure => ({
  startDate: draft.startDate,
  endDate: draft.endDate,
  reason: draft.reason.trim() === "" ? null : draft.reason.trim(),
});

const closureRange = ({ startDate, endDate }: SupplierClosure) =>
  startDate === endDate
    ? formatDay(startDate)
    : `${formatDay(startDate)} to ${formatDay(endDate)}`;

export const SupplierClosures = ({ supplierId }: { supplierId: string }) => {
  const { data: closures = [], isLoading } = useGetSupplierClosures(supplierId);
  const create = useCreateSupplierClosure(supplierId);
  const update = useUpdateSupplierClosure(supplierId);
  const remove = useDeleteSupplierClosure(supplierId);
  const [draft, setDraft] = useState<ClosureDraft>(EMPTY_DRAFT);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const error = isSubmitted ? draftError(draft) : null;
  const sorted = [...closures].sort((a, b) =>
    a.startDate.localeCompare(b.startDate),
  );

  const resetDraft = () => {
    setDraft(EMPTY_DRAFT);
    setEditingId(null);
    setIsSubmitted(false);
  };

  const onFailure = (failure: Error) =>
    notify.error(
      "Could not save the closure",
      getApiErrorMessage(failure, "Please try again"),
    );

  const handleSave = () => {
    setIsSubmitted(true);
    if (draftError(draft) !== null) return;

    const body = toClosureBody(draft);

    if (editingId === null) {
      create.mutate(body, { onSuccess: resetDraft, onError: onFailure });

      return;
    }

    update.mutate(
      { id: editingId, data: body },
      { onSuccess: resetDraft, onError: onFailure },
    );
  };

  const handleEdit = (closure: SupplierClosure) => {
    setEditingId(closure.id);
    setIsSubmitted(false);
    setDraft({
      startDate: closure.startDate,
      endDate: closure.endDate,
      reason: closure.reason ?? "",
    });
  };

  const handleDelete = (closure: SupplierClosure) =>
    remove.mutate(closure.id, {
      onSuccess: () => {
        if (editingId === closure.id) resetDraft();
      },
      onError: (failure) =>
        notify.error(
          "Could not delete the closure",
          getApiErrorMessage(failure, "Please try again"),
        ),
    });

  return (
    <div className="flex flex-col gap-4">
      {isLoading ? (
        <Skeleton className="h-12 w-full rounded-lg" />
      ) : sorted.length === 0 ? (
        <p className="text-muted text-sm">No closures</p>
      ) : (
        <ul className="divide-separator divide-y">
          {sorted.map((closure) => (
            <li key={closure.id} className="flex items-center gap-3 py-2.5">
              <div className="flex min-w-0 flex-1 flex-col leading-snug">
                <span className="text-sm font-medium tabular-nums">
                  {closureRange(closure)}
                </span>
                {closure.reason ? (
                  <span className="text-muted truncate text-xs">
                    {closure.reason}
                  </span>
                ) : null}
              </div>
              <Button
                isIconOnly
                aria-label="Edit closure"
                size="sm"
                variant="ghost"
                onPress={() => handleEdit(closure)}
              >
                <Pencil className="size-4" />
              </Button>
              <Button
                isIconOnly
                aria-label="Delete closure"
                className="text-(--danger)"
                isPending={remove.isPending && remove.variables === closure.id}
                size="sm"
                variant="ghost"
                onPress={() => handleDelete(closure)}
              >
                <Trash className="size-4" />
              </Button>
            </li>
          ))}
        </ul>
      )}

      <div className="bg-surface-secondary flex flex-col gap-3 rounded-2xl p-4">
        <span className="text-sm font-medium">
          {editingId === null ? "Add a closure" : "Edit closure"}
        </span>
        <PlainDateRangeField
          label="Closed days"
          name="closure"
          value={{ start: draft.startDate, end: draft.endDate }}
          onChange={({ start, end }) =>
            setDraft({ ...draft, startDate: start, endDate: end })
          }
        />
        <PlainTextField
          label="Reason"
          name="reason"
          placeholder="Eid al-Fitr"
          value={draft.reason}
          onChange={(reason) => setDraft({ ...draft, reason })}
        />
        {error ? (
          <p className="text-sm text-(--danger)" role="alert">
            {error}
          </p>
        ) : null}
        <div className="flex justify-end gap-2">
          {editingId !== null ? (
            <Button size="sm" variant="ghost" onPress={resetDraft}>
              Cancel
            </Button>
          ) : null}
          <Button
            isPending={create.isPending || update.isPending}
            size="sm"
            variant="secondary"
            onPress={handleSave}
          >
            {editingId === null ? "Add closure" : "Save closure"}
          </Button>
        </div>
      </div>
    </div>
  );
};
